import { normalizeFirstName, normalizeLastName } from '@massilia/features'
import { formatConditionText } from '@massilia/queries'
import { differenceInYears } from 'date-fns'
import { Check, UserCircle, X } from 'lucide-react'
import { toast } from 'sonner'

import { formatDateShort } from '@/utils/format-date'
import { GENDER_LABELS_FULL } from '@/utils/gender-labels'

// ── Helpers ──

const PASSENGER_TYPE_DISPLAY: Record<string, string> = {
  Adult: 'Adulte',
  Child: 'Enfant',
  Infant: 'Bébé',
}

export interface FlightSegmentCardProps {
  airline: string
  airlineLogoUrl?: null | string
  arrivalLabel: string
  departureLabel: string
  durationLabel: string
  journeyTypeLabel?: null | string
  route: string
  stopsLabel: string
  travelClass?: null | string
}

// ── PassengerCard ──

export interface PassengerCardProps {
  firstName: string
  lastName: string
  /** ISO date string */
  birthDate?: null | string
  gender?: null | string
  /** Explicit type. If omitted, derived from birthDate when available. */
  type?: 'Adult' | 'Child' | 'Infant' | null
}

export interface TicketConditionsProps {
  conditions: {
    exchangeable: { allowed: boolean; feeDescription: string }
    refundable: { allowed: boolean; feeDescription: string }
  }
  debugRule?: {
    ruleCode: string
    ruleId: string
    ruleSource: 'API_ONLY' | 'FALLBACK_M15' | 'OVERRIDE'
  }
}

// ── FlightSegmentCard ──

export function FlightSegmentCard({
  airline,
  airlineLogoUrl,
  arrivalLabel,
  departureLabel,
  durationLabel,
  journeyTypeLabel,
  route,
  stopsLabel,
  travelClass,
}: FlightSegmentCardProps) {
  return (
    <div className="border-gray-light-200 flex flex-col items-start rounded-sm border bg-white px-4 pt-2 pb-6">
      {/* Carrier */}
      {airlineLogoUrl ? (
        <div className="flex h-15.25 w-20.75 items-center justify-center p-4">
          <img
            alt={airline}
            className="max-h-full max-w-full object-contain"
            src={airlineLogoUrl}
          />
        </div>
      ) : (
        <p className="text-gray-light-700 px-1 py-3 text-xs font-medium">{airline}</p>
      )}

      <div className="flex flex-col gap-2">
        {/* Route */}
        <p className="text-gray-light-900 text-sm font-semibold">{route}</p>

        {/* Times */}
        <div className="text-gray-light-900 flex flex-wrap items-center gap-2 text-xs font-semibold">
          <span>
            {journeyTypeLabel && `${journeyTypeLabel} · `}
            {departureLabel}
          </span>
          <span>&bull;</span>
          <span>{arrivalLabel}</span>
        </div>

        {/* Details */}
        <div className="text-gray-light-600 flex flex-wrap items-center gap-2 text-xs font-medium">
          <span>{stopsLabel}</span>
          <span>&bull;</span>
          <span>Durée : {durationLabel}</span>
          {travelClass && (
            <>
              <span>&bull;</span>
              <span>Classe : {travelClass}</span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export function PassengerCard({
  firstName,
  lastName,
  birthDate,
  gender,
  type,
}: PassengerCardProps) {
  const resolvedType = type ?? (birthDate ? derivePassengerType(birthDate) : null)
  const typeLabel = resolvedType ? (PASSENGER_TYPE_DISPLAY[resolvedType] ?? null) : null
  const genderLabel = gender ? (GENDER_LABELS_FULL[gender] ?? null) : null
  const birthDateLabel = birthDate ? formatDateShort(birthDate) : null

  const metaItems: string[] = []
  if (typeLabel) metaItems.push(typeLabel)
  if (genderLabel) metaItems.push(genderLabel)
  if (birthDateLabel) metaItems.push(birthDateLabel)

  return (
    <div className="flex items-start gap-4">
      <UserCircle className="text-gray-light-600 size-6 shrink-0" />
      <div className="flex flex-col gap-2">
        <p className="text-gray-light-700 text-sm font-medium">
          {normalizeFirstName(firstName)} {normalizeLastName(lastName)}
        </p>
        {metaItems.length > 0 && (
          <div className="text-gray-light-600 flex flex-wrap items-center gap-2 text-xs font-medium">
            {metaItems.map((item, i) => (
              <span key={i}>
                {i > 0 && <span className="mr-2">&bull;</span>}
                {item}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── TicketConditions ──

export function TicketConditions({ conditions, debugRule }: TicketConditionsProps) {
  return (
    <div className="flex flex-col gap-1.5">
      {/* Exchangeable */}
      <div className="flex items-start gap-3">
        {conditions.exchangeable.allowed ? (
          <Check className="text-success-600 mt-0.5 size-3 shrink-0" />
        ) : (
          <X className="mt-0.5 size-3 shrink-0 text-red-600" />
        )}
        <ConditionText
          allowed={conditions.exchangeable.allowed}
          feeDescription={conditions.exchangeable.feeDescription}
          label="Billet modifiable"
        />
      </div>

      {/* Refundable */}
      <div className="flex items-start gap-3">
        {conditions.refundable.allowed ? (
          <Check className="text-success-600 mt-0.5 size-3 shrink-0" />
        ) : (
          <X className="mt-0.5 size-3 shrink-0 text-red-600" />
        )}
        <ConditionText
          allowed={conditions.refundable.allowed}
          feeDescription={conditions.refundable.feeDescription}
          label="Billet remboursable sans justificatif"
        />
      </div>

      {debugRule && (
        <div className="mt-2 rounded border border-teal-200 bg-teal-50 p-2 font-mono text-[10px] leading-3 text-teal-800">
          <span className="font-bold">Règle appliquée:</span> {debugRule.ruleCode} (
          <button
            className="cursor-pointer hover:underline"
            onClick={() => {
              void navigator.clipboard.writeText(debugRule.ruleId).then(() => {
                toast.info('ID copié')
              })
            }}
            title="Cliquez pour copier l'ID"
          >
            id: {debugRule.ruleId}
          </button>
          ) - {debugRule.ruleSource}
        </div>
      )}
    </div>
  )
}

function ConditionText({
  allowed,
  feeDescription,
  label,
}: {
  allowed: boolean
  feeDescription: string
  label: string
}) {
  const parts = formatConditionText({ allowed, feeDescription, label })
  return (
    <span className="text-gray-light-900 text-xs leading-4">
      {parts.map((part, i) =>
        part.bold ? (
          <strong key={i} className="font-bold">
            {part.text}
          </strong>
        ) : (
          <span key={i}>{part.text}</span>
        ),
      )}
    </span>
  )
}

/**
 * Derive passenger type from birth date using age thresholds:
 * - < 2 years  → Infant (Bébé)
 * - 2–11 years → Child (Enfant)
 * - ≥ 12 years → Adult (Adulte)
 */
function derivePassengerType(birthDate: string): 'Adult' | 'Child' | 'Infant' {
  const age = differenceInYears(new Date(), new Date(birthDate))
  if (age < 2) return 'Infant'
  if (age < 12) return 'Child'
  return 'Adult'
}
